import { stringify } from 'query-string';
import React, { useEffect } from 'react';

import { useTrackCustomEvent } from 'src/hooks/useTrackCustomEvent';
import { Wizard, WizardStep } from 'src/hooks/useWizard';

import {
  PersistedQueryListConfigureModal,
  PersistedQueryListConfigureModalState,
} from './persistedQueryListConfigureModal/PersistedQueryListConfigureModal';
import {
  PersistedQueryListLinkModal,
  PersistedQueryListLinkModalState,
} from './persistedQueryListLinkModal/PersistedQueryListLinkModal';
import {
  PersistedQueryListMetaModal,
  PersistedQueryListMetaModalState,
} from './persistedQueryListMetaModal/PersistedQueryListMetaModal';
import {
  PersistedQueryListPublishModal,
  PersistedQueryListPublishModalState,
} from './persistedQueryListPublishModal/PersistedQueryListPublishModal';
import { PersistedQueryListConfigStep } from './steps';

type PersistedQueryListConfigWizardState =
  | PersistedQueryListMetaModalState
  | PersistedQueryListLinkModalState
  | PersistedQueryListPublishModalState
  | PersistedQueryListConfigureModalState;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  __test?: {
    initialStepId?: PersistedQueryListConfigStep;
    initialState?: PersistedQueryListConfigWizardState;
  };
}

export const PersistedQueryListConfigWizard = ({
  isOpen,
  onClose,
  __test,
}: Props) => {
  const trackCustomEvent = useTrackCustomEvent();
  useEffect(() => {
    trackCustomEvent({
      category: 'Persisted Queries',
      action: 'wizard',
      label: stringify({ action: 'open' }),
    });
    return () => {
      trackCustomEvent({
        category: 'Persisted Queries',
        action: 'wizard',
        label: stringify({ action: 'close' }),
      });
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const wizardSteps = React.useMemo(
    (): MinLengthArray<1, WizardStep<PersistedQueryListConfigWizardState>> => [
      {
        id: PersistedQueryListConfigStep.MetaNameDesc,
        name: 'Create',
        content: ({ state, mergeState, goToNextStep, reset }) => (
          <PersistedQueryListMetaModal
            isOpen={isOpen}
            state={state}
            onComplete={(newState) => {
              mergeState(newState);
              goToNextStep();
              trackCustomEvent({
                category: 'Persisted Queries',
                action: 'wizard',
                label: stringify({ action: 'next', step: 'create' }),
              });
            }}
            onClose={() => {
              reset();
              onClose();
            }}
          />
        ),
      },
      {
        id: PersistedQueryListConfigStep.Link,
        name: 'Link',
        content: ({ state, mergeState, goToNextStep, reset }) => (
          <PersistedQueryListLinkModal
            isOpen={isOpen}
            state={state}
            onComplete={(linkedVariantId, linkedVariantName) => {
              mergeState({ linkedVariantId, linkedVariantName });
              goToNextStep();
              trackCustomEvent({
                category: 'Persisted Queries',
                action: 'wizard',
                label: stringify({
                  action: 'next',
                  step: 'link',
                  linked: !!linkedVariantId,
                }),
              });
            }}
            onClose={() => {
              reset();
              onClose();
            }}
          />
        ),
      },
      {
        id: PersistedQueryListConfigStep.Publish,
        name: 'Publish',
        content: ({ state, goToNextStep, goToPreviousStep, reset }) => (
          <PersistedQueryListPublishModal
            isOpen={isOpen}
            state={state}
            onPrevious={() => {
              goToPreviousStep();
              trackCustomEvent({
                category: 'Persisted Queries',
                action: 'wizard',
                label: stringify({
                  action: 'previous',
                  step: 'publish',
                }),
              });
            }}
            onComplete={() => {
              goToNextStep();
              trackCustomEvent({
                category: 'Persisted Queries',
                action: 'wizard',
                label: stringify({
                  action: 'next',
                  step: 'publish',
                }),
              });
            }}
            onClose={() => {
              reset();
              onClose();
            }}
          />
        ),
      },
      {
        id: PersistedQueryListConfigStep.Configure,
        name: 'Configure',
        content: ({ state, goToPreviousStep, reset }) => (
          <PersistedQueryListConfigureModal
            isOpen={isOpen}
            state={state}
            onPrevious={() => {
              goToPreviousStep();
              trackCustomEvent({
                category: 'Persisted Queries',
                action: 'wizard',
                label: stringify({
                  action: 'previous',
                  step: 'configure',
                }),
              });
            }}
            onComplete={() => {
              reset();
              trackCustomEvent({
                category: 'Persisted Queries',
                action: 'wizard',
                label: stringify({
                  action: 'finish',
                  step: 'configure',
                }),
              });
              onClose();
            }}
          />
        ),
      },
    ],
    [isOpen, trackCustomEvent, onClose],
  );
  return (
    <Wizard<PersistedQueryListConfigWizardState>
      {...__test}
      steps={wizardSteps}
    />
  );
};
